import { useCosAgentName } from "@app/lib/swr/cos_agent_name";
import {
  useInstinctAction,
  useInstinctConnection,
} from "@app/lib/swr/instinct";
import type {
  GetInstinctResponseBody,
  InstinctConnectionStateType,
} from "@app/types/api/me/instinct";
import type { LightWorkspaceType } from "@app/types/user";
import {
  ArrowLeft,
  Button,
  CheckCircle,
  Circle,
  Clipboard,
  ClipboardCheck,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  Spinner,
  useCopyToClipboard,
} from "@dust-tt/sparkle";
import { QRCodeSVG } from "qrcode.react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

const instinctPollMs = 3000;
// Instinct often takes a few minutes to send its first mail, so a shorter wait reads as a failure.
const stalledAfterMs = 300_000;
const tickMs = 1000;

// SWR restarts its polling timer whenever refreshInterval changes identity, so this must be stable.
function instinctRefreshInterval(data?: GetInstinctResponseBody): number {
  return data?.connection?.status === "active" ? 0 : instinctPollMs;
}

type StepKey = keyof InstinctConnectionStateType["steps"];

const stepOrder: StepKey[] = [
  "codeReady",
  "codeReceived",
  "connectedMailSent",
  "confirmed",
];

function stepLabel(step: StepKey, cosName: string): string {
  switch (step) {
    case "codeReady":
      return "Code ready";
    case "codeReceived":
      return `Instinct sent your code to ${cosName}`;
    case "connectedMailSent":
      return `${cosName} replied to Instinct`;
    case "confirmed":
      return `You got ${cosName}'s message on WhatsApp`;
  }
}

function stalledHint(step: StepKey, cosName: string): string | null {
  switch (step) {
    case "codeReady":
      return null;
    case "codeReceived":
      return "Still waiting for Instinct's email. It can take a few minutes. If the message didn't reach Instinct on WhatsApp, copy it and send it again.";
    case "connectedMailSent":
      return `Still waiting for ${cosName}'s reply to Instinct. Start over if this keeps waiting.`;
    case "confirmed":
      return `Nothing on WhatsApp yet? Instinct may not be forwarding ${cosName}'s mail. Start over to try again.`;
  }
}

function trustLines(cosName: string): string[] {
  return [
    `${cosName} only listens to your Instinct. Mail from anyone else gets ignored.`,
    `${cosName} checks Instinct's digital signature on every message, so nobody can pretend to be it.`,
    `Messages travel encrypted between Instinct and ${cosName}.`,
    `${cosName} sends Instinct only what concerns you, and never passwords or keys.`,
    `Disconnect any time. ${cosName} stops listening at once.`,
  ];
}

function formatRemaining(remainingSeconds: number): string {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function isTouchDevice(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

interface CopyMessageButtonProps {
  message: string;
}

function CopyMessageButton({ message }: CopyMessageButtonProps) {
  const [isCopied, copy] = useCopyToClipboard();
  return (
    <>
      <Button
        label={isCopied ? "Copied" : "Copy message"}
        icon={isCopied ? ClipboardCheck : Clipboard}
        variant="outline"
        size="md"
        className="h-11"
        type="button"
        onClick={() => copy(message)}
      />
      <span className="sr-only" aria-live="polite">
        {isCopied ? "Message copied" : ""}
      </span>
    </>
  );
}

interface StepRowProps {
  label: string;
  state: "done" | "current" | "upcoming";
  children?: ReactNode;
}

function StepRow({ label, state, children }: StepRowProps) {
  return (
    <li
      aria-current={state === "current" ? "step" : undefined}
      className="flex min-h-11 items-center gap-3"
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        {state === "done" && (
          <Icon
            visual={CheckCircle}
            size="sm"
            className="text-emerald-600 dark:text-emerald-400"
          />
        )}
        {state === "current" && <Spinner size="xs" />}
        {state === "upcoming" && (
          <Icon
            visual={Circle}
            size="sm"
            className="text-stone-300 dark:text-stone-600"
          />
        )}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 text-sm",
          state === "upcoming"
            ? "text-muted-foreground dark:text-stone-500"
            : "font-medium text-foreground dark:text-stone-100"
        )}
      >
        {label}
        <span className="sr-only">
          {state === "done"
            ? " (done)"
            : state === "current"
              ? " (in progress)"
              : " (not started)"}
        </span>
      </span>
      {children}
    </li>
  );
}

interface ConnectInstinctBodyProps {
  owner: LightWorkspaceType;
  onBack?: () => void;
  onClose: () => void;
}

export function ConnectInstinctBody({
  owner,
  onBack,
  onClose,
}: ConnectInstinctBodyProps) {
  const { agentName: cosName } = useCosAgentName();
  const { connection } = useInstinctConnection({
    owner,
    refreshInterval: instinctRefreshInterval,
  });
  const act = useInstinctAction({ owner });
  const [isTouch] = useState(isTouchDevice);
  const [isStarting, setIsStarting] = useState(true);
  const [startFailed, setStartFailed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const hasStarted = useRef(false);

  const start = async (kind: "connect" | "new_address") => {
    setIsStarting(true);
    setStartFailed(false);
    const result = await act({ kind });
    setStartFailed(result === undefined);
    setIsStarting(false);
  };

  // Opening the dialog is what asks the server for a code: a pending connection gets a fresh
  // one, a verifying or active one is left as it is.
  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    void start("connect");
  });

  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), tickMs);
    return () => clearInterval(interval);
  }, []);

  const steps = connection?.steps;
  const currentIndex = steps ? stepOrder.findIndex((key) => !steps[key]) : 0;
  const [trackedIndex, setTrackedIndex] = useState(currentIndex);
  const [stepStartedAtMs, setStepStartedAtMs] = useState(nowMs);
  if (trackedIndex !== currentIndex) {
    setTrackedIndex(currentIndex);
    setStepStartedAtMs(nowMs);
  }

  // The body is a plain scroller, not DialogContainer: its ScrollArea viewport is h-full, which an
  // auto-height dialog never resolves, so the expanded safety row would be clipped.
  const header = (
    <DialogHeader className="border-b border-stone-100 bg-white px-6 pb-4 pt-5 dark:border-white/10 dark:bg-[#0a0a0a]">
      <div className="flex items-center gap-2.5 pr-6">
        {onBack && (
          <Button
            icon={ArrowLeft}
            variant="ghost"
            size="xs"
            type="button"
            aria-label="Back"
            onClick={onBack}
            className="h-8 w-8 shrink-0 rounded-lg text-muted-foreground hover:text-foreground dark:text-stone-400 dark:hover:text-stone-100"
          />
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <DialogTitle className="text-lg font-bold tracking-tight text-foreground dark:text-white">
            Connect Instinct
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed text-muted-foreground dark:text-stone-400">
            Ask {cosName} for work from WhatsApp. Instinct emails {cosName} for
            you and passes {cosName}&apos;s replies back.
          </DialogDescription>
        </div>
      </div>
    </DialogHeader>
  );

  if (startFailed && !connection) {
    return (
      <>
        {header}
        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5 dark:bg-[#0a0a0a]">
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-foreground dark:text-stone-200">
              Memoair couldn&apos;t create a pairing code.
            </p>
            <Button
              label="Try again"
              variant="primary"
              size="md"
              className="h-11"
              onClick={() => void start("connect")}
            />
          </div>
        </div>
      </>
    );
  }

  if (!connection || !steps || (isStarting && !steps.codeReady)) {
    return (
      <>
        {header}
        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5 dark:bg-[#0a0a0a]">
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        </div>
      </>
    );
  }

  const isConnected = connection.status === "active";
  const currentStep = currentIndex === -1 ? null : stepOrder[currentIndex];
  const remainingSeconds = connection.codeExpiresAt
    ? Math.max(
        0,
        Math.ceil((Date.parse(connection.codeExpiresAt) - nowMs) / 1000)
      )
    : 0;
  const isCodeLive =
    connection.code !== null && !steps.codeReceived && remainingSeconds > 0;
  const isCodeExpired = !steps.codeReceived && !isCodeLive;
  const hint =
    currentStep && nowMs - stepStartedAtMs > stalledAfterMs
      ? stalledHint(currentStep, cosName)
      : null;
  const startOver = () =>
    void start(connection.status === "pending" ? "connect" : "new_address");

  const confirm = async () => {
    setIsConfirming(true);
    await act({ kind: "confirm" });
    setIsConfirming(false);
  };

  const qr = connection.whatsAppUrl ? (
    <div className="flex flex-col items-center gap-2" key="qr">
      <div className="rounded-xl border border-stone-200 bg-white p-3 dark:border-white/15">
        <QRCodeSVG
          value={connection.whatsAppUrl}
          size={168}
          title="QR code that opens your Instinct chat on WhatsApp"
        />
      </div>
      <span className="text-xs text-muted-foreground dark:text-stone-400">
        Scan with your phone&apos;s camera
      </span>
    </div>
  ) : null;

  const openButton = connection.whatsAppUrl ? (
    <Button
      key="open"
      label="Open WhatsApp"
      variant="primary"
      size="md"
      className="h-11 w-full"
      href={connection.whatsAppUrl}
      target="_blank"
      rel="noopener noreferrer"
    />
  ) : null;

  return (
    <>
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-5 dark:bg-[#0a0a0a]">
        <div className="flex flex-col gap-5">
          <span className="sr-only" role="status" aria-atomic="true">
            {isConnected
              ? "Connected"
              : currentStep
                ? `Waiting: ${stepLabel(currentStep, cosName)}`
                : ""}
          </span>

          {isConnected ? (
            <div className="flex flex-col gap-1 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-400/30 dark:bg-emerald-400/10">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground dark:text-white">
                <Icon
                  visual={CheckCircle}
                  size="sm"
                  className="text-emerald-600 dark:text-emerald-400"
                />
                Connected
              </span>
              <span className="text-sm text-muted-foreground dark:text-stone-300">
                Message Instinct on WhatsApp and {cosName} picks it up.
              </span>
            </div>
          ) : (
            !steps.codeReceived && (
              <div className="flex flex-col gap-4 rounded-xl border border-stone-200/80 bg-stone-50/60 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                {isCodeLive ? (
                  <>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-mono text-2xl font-semibold tracking-[0.2em] text-foreground dark:text-white">
                        {connection.code}
                      </span>
                      <span className="text-xs text-muted-foreground dark:text-stone-400">
                        <span aria-hidden="true">
                          Expires in {formatRemaining(remainingSeconds)}
                        </span>
                        <span className="sr-only">
                          Expires in {Math.ceil(remainingSeconds / 60)} minutes
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-foreground dark:text-stone-200">
                      Send the prefilled message to Instinct. It emails this
                      code to {cosName}.
                    </p>
                    {qr || openButton ? (
                      <div className="flex flex-col items-stretch gap-3">
                        {isTouch ? [openButton, qr] : [qr, openButton]}
                      </div>
                    ) : (
                      connection.pairingMessage && (
                        <div className="flex flex-col gap-2">
                          <p className="rounded-lg border border-stone-200 bg-white p-3 text-sm text-foreground dark:border-white/15 dark:bg-black/40 dark:text-stone-200">
                            {connection.pairingMessage}
                          </p>
                          <CopyMessageButton
                            message={connection.pairingMessage}
                          />
                        </div>
                      )
                    )}
                  </>
                ) : (
                  isCodeExpired && (
                    <div className="flex flex-col items-start gap-3">
                      <p className="text-sm text-foreground dark:text-stone-200">
                        This code has expired.
                      </p>
                      <Button
                        label="Get a new code"
                        variant="primary"
                        size="md"
                        className="h-11"
                        isLoading={isStarting}
                        onClick={startOver}
                      />
                    </div>
                  )
                )}
              </div>
            )
          )}

          <ol className="flex flex-col gap-1" aria-label="Connection progress">
            {stepOrder.map((key, index) => (
              <StepRow
                key={key}
                label={stepLabel(key, cosName)}
                state={
                  steps[key]
                    ? "done"
                    : index === currentIndex
                      ? "current"
                      : "upcoming"
                }
              >
                {key === "confirmed" && !isConnected && (
                  <Button
                    label="I got it"
                    variant={steps.connectedMailSent ? "primary" : "outline"}
                    size="md"
                    className="h-11"
                    disabled={!steps.connectedMailSent}
                    isLoading={isConfirming}
                    onClick={() => void confirm()}
                  />
                )}
              </StepRow>
            ))}
          </ol>

          {hint && !isConnected && (
            <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-400/30 dark:bg-amber-400/10">
              <p className="text-sm text-foreground dark:text-stone-200">
                {hint}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  label="Start over"
                  variant="outline"
                  size="md"
                  className="h-11"
                  isLoading={isStarting}
                  onClick={startOver}
                />
                {connection.pairingMessage && (
                  <CopyMessageButton message={connection.pairingMessage} />
                )}
              </div>
            </div>
          )}

          <Collapsible>
            <CollapsibleTrigger
              label="How your messages stay safe"
              className="min-h-11"
            />
            <CollapsibleContent>
              <ul className="flex list-disc flex-col gap-1.5 pl-5 pt-2 text-sm text-muted-foreground dark:text-stone-400">
                {trustLines(cosName).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>

          {isConnected && (
            <div className="flex justify-end">
              <Button
                label="Done"
                variant="primary"
                size="md"
                className="h-11"
                onClick={onClose}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface ConnectInstinctDialogProps {
  owner: LightWorkspaceType;
  isOpen: boolean;
  onClose: () => void;
}

export function ConnectInstinctDialog({
  owner,
  isOpen,
  onClose,
}: ConnectInstinctDialogProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        size="md"
        className="overflow-hidden rounded-2xl border border-stone-200/90 bg-white p-0 shadow-2xl shadow-black/80 dark:border-white/20 dark:bg-[#0a0a0a]"
      >
        {isOpen ? (
          <ConnectInstinctBody owner={owner} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
